"use client"

import { useEffect, useRef, useState } from "preact/hooks"
import { Deduction, DeductionSearchResult, searchDeductions, lookupDeductionById } from "@/api/deduction.tsx"
import { CheckSearchResult, lookupRemoteReferences, RemoteReference, searchChecks } from "@/api/accounting.tsx"
import { exactCurrencyFormatter, format_transaction_amount } from "@/utils"
import { useLocation } from "wouter-preact"
import { capitalCase } from "change-case"
import { format, parseISO } from "date-fns"
import { check_detail_path } from "@/accounting/check_path"
import { REMOTE_DOC_LABEL, remote_reference_link } from "@/accounting/remote_reference"

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function is_uuid(query: string): boolean {
  return UUID_REGEX.test(query)
}

/// Convert a full Deduction (from the UUID lookup endpoint) into a search result row.
export function deduction_to_search_result(deduction: Deduction): DeductionSearchResult {
  return {
    id: deduction.id,
    invoice_number: deduction.invoice_number,
    invoice_date: deduction.invoice_date,
    invoice_amount: deduction.invoice_amount,
    deduction_amount:
      deduction.kind === "invoice"
        ? Math.abs(deduction.discount_amount ?? deduction.invoice_amount)
        : Math.abs(deduction.invoice_amount),
    discount_amount: deduction.discount_amount ?? undefined,
    gross_amount: deduction.gross_amount ?? undefined,
    kind: deduction.kind,
    retailer_name: deduction.retailer_name ?? undefined,
    po_number: deduction.po_number,
    category: "",
    category_description: "",
    description: deduction.description ?? undefined,
    source: deduction.source,
  }
}

export interface SearchResults {
  checks: CheckSearchResult[]
  deductions: DeductionSearchResult[]
  documents: RemoteReference[]
}

const EMPTY_RESULTS: SearchResults = { checks: [], deductions: [], documents: [] }

async function run_search(query: string): Promise<SearchResults> {
  // A pasted UUID goes to the lookup endpoint (with admin session-switching)
  if (is_uuid(query)) {
    try {
      const deduction = await lookupDeductionById(query)
      return { checks: [], deductions: deduction ? [deduction_to_search_result(deduction)] : [], documents: [] }
    } catch {
      return EMPTY_RESULTS
    }
  }
  const [deductions, checks, documents] = await Promise.all([
    searchDeductions({ q: query }).catch(() => [] as DeductionSearchResult[]),
    searchChecks(query).catch(() => [] as CheckSearchResult[]),
    lookupRemoteReferences(query).catch(() => [] as RemoteReference[]),
  ])
  return { checks, deductions, documents }
}

function format_date(date: string | undefined): string | null {
  if (!date) return null
  try {
    return format(parseISO(date), "MMM d, yyyy")
  } catch {
    return null
  }
}

const KIND_BADGE_CLASSES: Record<string, string> = {
  invoice: "bg-emerald-50 text-emerald-700",
  deduction: "bg-amber-50 text-amber-700",
  repayment: "bg-blue-50 text-blue-700",
  prepayment: "bg-gray-100 text-gray-600",
}

function TypeBadge({ label, className }: { label: string; className: string }) {
  return (
    <span class={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${className}`}>
      {label}
    </span>
  )
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div class="sticky top-0 bg-gray-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
      {title}
    </div>
  )
}

interface ResultRowProps {
  onSelect: () => void
  children: React.ReactNode
}

function ResultRow({ onSelect, children }: ResultRowProps) {
  return (
    <li
      class="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
      // onMouseDown so selection wins the race against the input's blur
      onMouseDown={(e: MouseEvent) => {
        e.preventDefault()
        onSelect()
      }}>
      {children}
    </li>
  )
}

export function GlobalSearchBar() {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS)
  const [searched, setSearched] = useState(false)
  const [open, setOpen] = useState(false)
  const request_seq = useRef(0)
  const [, setLocation] = useLocation()

  useEffect(() => {
    const seq = ++request_seq.current
    if (query === "") {
      setResults(EMPTY_RESULTS)
      setSearched(false)
      return
    }
    const timer = setTimeout(async () => {
      const next = await run_search(query)
      if (request_seq.current === seq) {
        setResults(next)
        setSearched(true)
        setOpen(true)
      }
    }, 200)
    return () => clearTimeout(timer)
  }, [query])

  function close_and_reset() {
    setQuery("")
    setResults(EMPTY_RESULTS)
    setSearched(false)
    setOpen(false)
  }

  function select_deduction(id: string) {
    setLocation(`/deduction/${id}`)
    close_and_reset()
  }

  function select_check(number: string) {
    // Check numbers can contain '#' and spaces (e.g. "ACH# 123") — encode or the '#' truncates the URL
    setLocation(check_detail_path(number))
    close_and_reset()
  }

  function select_document(document: RemoteReference) {
    setLocation(remote_reference_link(document))
    close_and_reset()
  }

  const has_results = results.checks.length > 0 || results.deductions.length > 0 || results.documents.length > 0
  const show_dropdown = open && query !== "" && searched

  return (
    <div className="flex md:w-80">
      <div className="relative w-full">
        <input
          type="text"
          value={query}
          onChange={(e: any) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          placeholder="Search invoices, POs, checks, NetSuite IDs..."
          className="w-full px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          aria-label="Search"
        />
        {show_dropdown && (
          <div class="absolute left-0 z-50 mt-1 max-h-[70vh] w-[calc(100vw-5rem)] overflow-auto rounded-md border border-gray-200 bg-white shadow-xl sm:w-[34rem]">
            {results.documents.length > 0 && (
              <>
                <SectionHeader title="Accounting documents" />
                <ul>
                  {results.documents.map(document => (
                    <ResultRow
                      key={`${document.kind}:${document.remote_id}:${document.check_number}:${document.deduction_id}`}
                      onSelect={() => select_document(document)}>
                      <div class="flex min-w-0 items-center gap-2">
                        <TypeBadge label={REMOTE_DOC_LABEL[document.kind]} className="bg-violet-50 text-violet-700" />
                        <div class="flex min-w-0 flex-col">
                          <span class="truncate font-medium">{document.doc_number ?? document.remote_id}</span>
                          <span class="truncate text-xs text-gray-500">
                            {[document.customer_name, `ID ${document.remote_id}`].filter(Boolean).join(" · ")}
                          </span>
                        </div>
                      </div>
                      <div class="shrink-0 text-right">
                        {document.amount != null && (
                          <span class="text-gray-700">{exactCurrencyFormatter(document.amount)}</span>
                        )}
                        <span class="block text-xs text-gray-400">{format_date(document.date ?? undefined)}</span>
                      </div>
                    </ResultRow>
                  ))}
                </ul>
              </>
            )}
            {results.checks.length > 0 && (
              <>
                <SectionHeader title="Checks" />
                <ul>
                  {results.checks.map(check => (
                    <ResultRow key={check.number} onSelect={() => select_check(check.number)}>
                      <div class="flex min-w-0 items-center gap-2">
                        <TypeBadge label="Check" className="bg-indigo-50 text-indigo-700" />
                        <div class="flex min-w-0 flex-col">
                          <span class="truncate font-medium">{check.number}</span>
                          <span class="truncate text-xs text-gray-500">
                            {check.customer_name}
                            {" · "}
                            {check.deduction_count} transaction{check.deduction_count === 1 ? "" : "s"}
                          </span>
                        </div>
                      </div>
                      <div class="shrink-0 text-right">
                        {check.amount != null && <span class="text-gray-700">{exactCurrencyFormatter(check.amount)}</span>}
                        <span class="block text-xs text-gray-400">{format_date(check.date)}</span>
                      </div>
                    </ResultRow>
                  ))}
                </ul>
              </>
            )}
            {results.deductions.length > 0 && (
              <>
                <SectionHeader title="Deductions & Invoices" />
                <ul>
                  {results.deductions.map(ded => (
                    <ResultRow key={ded.id} onSelect={() => select_deduction(ded.id)}>
                      <div class="flex min-w-0 items-center gap-2">
                        <TypeBadge
                          label={capitalCase(ded.kind)}
                          className={KIND_BADGE_CLASSES[ded.kind] ?? "bg-gray-100 text-gray-600"}
                        />
                        <div class="flex min-w-0 flex-col">
                          <span class="truncate font-medium">{ded.invoice_number}</span>
                          <span class="truncate text-xs text-gray-500">
                            {[ded.customer_name, ded.po_number ? `PO: ${ded.po_number}` : null]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      </div>
                      <div class="shrink-0 text-right">
                        <span class="text-gray-700">
                          {format_transaction_amount(ded.kind, ded.deduction_amount ?? ded.invoice_amount)}
                        </span>
                        <span class="block text-xs text-gray-400">{format_date(ded.invoice_date)}</span>
                      </div>
                    </ResultRow>
                  ))}
                </ul>
              </>
            )}
            {!has_results && (
              <div class="px-4 py-3 text-sm text-gray-500">No matching checks, invoices, deductions or NetSuite documents.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
